import axios from 'axios'
import router from '@/router/index.ts'
import { MessageBox } from 'element-ui'
import i18n from '@/resources/index.js'
import { applySecurityHeaders } from './securityHeaders'
import { fields } from '@/shared/constants'

// 超时配置常量
const TIMEOUT_EXPORT = 300000 // 导出请求超时时间: 5分钟
const TIMEOUT_DEFAULT = 120000 // 默认请求超时时间: 2分钟

const service = axios.create({
  // process.env.NODE_ENV === 'development' 来判断是否开发环境
  // easy-mock服务挂了，暂时不使用了
  // baseURL: 'https://www.easy-mock.com/mock/592501a391470c0ac1fab128',
  timeout: TIMEOUT_DEFAULT, // 默认2分钟超时
  paramsSerializer: {
    indexes: null, // 数组参数使用 repeat 格式：key=1&key=2（ASP.NET Core 兼容）
  },
})

// 标记是否正在跳转登录页,避免多个请求同时触发多次跳转
let isRedirecting = false

service.interceptors.request.use(
  (config) => {
    //修复中文串码问题
    config.headers['language'] = localStorage.getItem('language')

    // 注意: Accept-Encoding 是浏览器自动管理的安全头，不需要手动设置
    // 浏览器会自动在支持的情况下添加 gzip, deflate, br 等压缩方式
    // config.headers['Accept-Encoding'] = 'gzip, deflate, br'  // ❌ 已移除

    // 根据 URL 动态设置超时时间
    // 包含 'Export' 的请求使用 5 分钟超时，其他使用 2 分钟
    if (config.url && config.url.includes('Export')) {
      config.timeout = TIMEOUT_EXPORT
    }

    // 不需要 token 的接口白名单
    const noTokenUrls = [
      'Login/UserLogin',
      'Login/UserLoginWithCaptcha', // 带验证码的登录接口
      'Login/GetCaptcha',            // 获取验证码接口
      'Login/RecoverPassword',        // 找回密码接口
      'Login/ResetPassword',          // 登录密码过期后的重置接口
      'Login/GetPasswordInputMetadata', // 密码规则元数据接口
      'Login/SSOLogin',               // SSO 登录接口
      'Login/SSOBindLogin',           // SSO 绑定登录接口
      '/Domain/CheckDomain'
    ]

    // 判断是否是白名单接口
    const isNoTokenUrl = noTokenUrls.some(url => config.url.includes(url))

    // 安全整改(2026-08)：敏感参数统一移至请求头（共享逻辑，详见 securityHeaders.js）
    applySecurityHeaders(config)

    // 判断本地的cookie中是否有token
    if (!isNoTokenUrl) {
      if (localStorage.getItem('ms_token') != null && localStorage.getItem('ms_token') !== '') {
        config.headers['Authorization'] = 'Bearer ' + localStorage.getItem('ms_token')

        config.headers['Accept-Language'] = localStorage.getItem('language')
      } else {
        // 没有 token 时，取消需要认证的请求
        const cancelToken = axios.CancelToken
        config.cancelToken = new cancelToken((cancel) => {
          cancel('Request cancelled: no token available')
        })
        return config
      }
    }

    return config
  },
  (error) => {
    console.log(error)
    return Promise.reject()
  }
)

service.interceptors.response.use(
  (response) => {
    if (response.status === 200) {
      return response.data
    } else {
      Promise.reject()
    }
  },
  (error) => {
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    console.log('Response Error:', error)

    // 处理 JWT Token 过期 (401 Unauthorized)
    if (error.response && error.response.status === 401) {
      handleTokenExpired()
      return Promise.reject(error)
    }

    // 处理 403 Forbidden (Token 无效或权限不足)
    if (error.response && error.response.status === 403) {
      handleTokenExpired()
      return Promise.reject(error)
    }

    // 处理后端返回的业务错误 (某些后端在 200 状态下返回错误)
    if (error.response && error.response.data) {
      const data = error.response.data
      // 根据你后端的实际返回格式调整
      if (data.code === -1 || data.code === 401 || data.Value === 0) {
        handleTokenExpired()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

/**
 * 处理 Token 过期
 * - 清除本地存储
 * - 显示提示消息
 * - 跳转到登录页
 */
function handleTokenExpired() {
  // 避免重复跳转
  if (isRedirecting) {
    return
  }
  isRedirecting = true

  // 清除 token 和用户信息
  localStorage.removeItem('ms_token')
  localStorage.removeItem(fields.userInfo)
  localStorage.removeItem('userInfo')
  localStorage.removeItem('sssid')

  // 显示提示消息 (支持多语言)
  MessageBox.alert(i18n.t('login.tokenExpired'), i18n.t('login.tips'), {
    confirmButtonText: i18n.t('login.confirm'),
    type: 'warning',
    showClose: false,
    callback: () => {
      // 跳转到登录页
      router.replace('/loginPage')
      // 重置标记
      setTimeout(() => {
        isRedirecting = false
      }, 1000)
    },
  })
}

export default service
